import { API_BASE } from './apiBase';
import { GIVE_UP_CLOSE, SERVER_NOTE_CODES, closeText, noteText } from './monitorNotes';
// ============================================================================
// scrcpyStream.js  --  Route B live-screen engine for the Phone Monitor grid.
//
// WHY THIS EXISTS (and why V2 failed):
//   ws-scrcpy NEVER reads a `quality`/`bitrate` URL param. V2 appended
//   `&quality=low` to the iframe hash; it was ignored, so every card streamed at
//   the default bitrate. Tiers were decorative.
//
//   The bitrate/size/fps of a scrcpy stream is controlled by a binary control
//   message the CLIENT sends over the video WebSocket:
//     ControlMessage.TYPE_CHANGE_STREAM_PARAMETERS (=101) + a 35-byte
//     VideoSettings struct  (see ControlMessage.ts / VideoSettings.ts in the fork).
//   The scrcpy server re-encodes at the new bitrate IN PLACE on the SAME socket,
//   no reconnect. That is the whole trick, and it is measured/proven in the fork.
//
//   So instead of an <iframe> we can never steer, this page owns the WebSocket
//   itself: connect proxy-adb -> send LOW settings to start -> decode H.264 with
//   WebCodecs onto a <canvas>. To raise ONE phone to HIGH we send a new
//   TYPE_CHANGE_STREAM_PARAMETERS on that phone's existing socket. Siblings are
//   left untouched, so they keep streaming LOW. Collapse sends LOW again.
//
// WIRE FORMAT (verified against the fork source + an on-device probe):
//   * Server->client binary messages:
//       - starts with ASCII "scrcpy_initial" -> device/display info (start gate)
//       - starts with ASCII "scrcpy_message" -> device message (clipboard, ignore)
//       - anything else                       -> ONE H.264 NAL unit (Annex-B)
//     The very first video message bundles SPS(7)+PPS(8); then an IDR(5); then
//     P-slices(1). Each message is a complete NAL unit (probe: naluTypes=[7,8],
//     [5], [1], ...), so we can feed WebCodecs directly, exactly like the fork's
//     WebCodecsPlayer does.
//   * Client->server: 1 byte type (101) + 35-byte VideoSettings struct.
//
// This module is framework-agnostic (plain class + a manager). PhoneMonitorPage
// drives it through refs; the Playwright proof harness loads the very same file.
// ============================================================================

// scrcpy server listens on this device-side TCP port (fork Constants.SERVER_PORT).
export const SCRCPY_REMOTE_PORT = 8886;

// Base ws:// origin of the running ws-scrcpy fork. Configurable for prod.
// LOCAL transport: the browser is ON the customer's PC and talks to ws-scrcpy directly.
export const WSSCRCPY_BASE =
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_WSSCRCPY_BASE) ||
    'ws://localhost:6847';

// REMOTE transport (M4b): the browser is anywhere and reaches the phone through Spring's binary
// viewer WebSocket, which relays BYTE-IDENTICAL scrcpy bytes from the customer's BM. Same decoder,
// same 36-byte control message - only the socket TARGET differs. The Spring API base is the same one
// api.js uses; the viewer path is /uiapi/bm/vview/{installId}?serial=..
// Dev builds IGNORE REACT_APP_API_BASE - the preview harness injects one pointing at :8081
// (a server that is usually not running), and a process env var beats every .env file in CRA.
// That is the exact bug that broke login for a day (lib/api.js, commit 154db80); this file had
// its own private copy of the base and kept the landmine. Dev dials the Spring server directly
// because the dev proxy has ws:false (WebSockets are deliberately not proxied).
// 2026-09-08: resolved through lib/apiBase.js, which VALIDATES the env var's shape instead of
// only defaulting it when absent. A Git Bash build turned REACT_APP_API_BASE=/uiapi into
// "C:/Program Files/Git/uiapi" (MSYS path conversion), that string became axios's baseURL, and
// Chrome refused every call as a file:// load - the whole app was unreachable behind its login.
// Dev still dials Spring directly - setupProxy.js has ws:false, so a relative base cannot work
// for a WebSocket here. Production shares the validated base with every other caller.
export const SPRING_API_BASE =
    (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development')
        ? 'http://localhost:8080/uiapi'
        : API_BASE;

// 'local' (default, byte-for-byte the proven on-PC path) or 'remote' (through Spring). Env-selectable
// so nothing about local mode changes unless remote is explicitly turned on.
// 2026-09-29 (monitor video self-heal): this constant is ALWAYS 'local' in a production bundle -
// `typeof process` is 'undefined' in a browser (CRA's DefinePlugin replaces process.env.X, not
// `typeof process`), so REACT_APP_MONITOR_TRANSPORT / REACT_APP_WSSCRCPY_BASE never reach it. The
// Monitor page no longer reads it: the route is chosen at RUN time by lib/monitorConfig.js. Kept
// only as the dev-harness value (resolver step 4) and for older callers.
export const MONITOR_TRANSPORT =
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_MONITOR_TRANSPORT) ||
    'local';

// Quality tiers. maxSize is a square bounding box; scrcpy fits the device aspect
// inside it, so a 720x1600 phone at maxSize 480 encodes ~216x480.
//   LOW  is what EVERY card on the page streams continuously.
//   HIGH is what a single card is raised to while expanded.
export const QUALITY = {
    low: { bitrate: 800000, maxFps: 15, maxSize: 480, iFrameInterval: 10 },
    high: { bitrate: 8000000, maxFps: 30, maxSize: 1080, iFrameInterval: 10 },
    // r16q11: the one phone beside a chat, where its text has to be read. On the server route it stays the config's remoteHigh
    sharp: { bitrate: 12000000, maxFps: 30, maxSize: 2000, iFrameInterval: 10 },
};

// Owner decision D5 (2026-09-29): on the SERVER route only, the grid tiles stream lower - about
// 400 kbps / 10 fps / 360p - because every byte crosses our server twice. The expanded single view
// stays sharp (QUALITY.high) on every route, and the direct route keeps QUALITY.low. The server
// config may send its own `remoteLow` (lib/monitorConfig.js clamps it).
export const QUALITY_REMOTE_LOW = { bitrate: 400000, maxFps: 10, maxSize: 360, iFrameInterval: 10 };

// Self-heal timings (design V2). Exported so tests and the page read the same numbers.
export const HEAL = Object.freeze({
    FAST_DELAYS_MS: [1000, 2000, 4000, 8000, 15000, 15000, 15000, 15000], // 8 tries, ~75 s
    SLOW_DELAY_MS: 60000, // then every 60 s ...
    SLOW_TRIES: 30, // ... 30 times (30 min), only while the tab is visible; then Paused
    JITTER: 0.4, // every delay +-40% (review M5: no reconnect wave after a server restart)
    OPEN_TIMEOUT_MS: 10000, // socket not open in 10 s -> close and retry
    INITIAL_TIMEOUT_MS: 20000, // open, no scrcpy_initial and no note in 20 s -> close and retry
    PROBE_AFTER_MS: 15000, // no frame for 15 s -> liveness probe (re-send the 36-byte settings)
    PROBE_MIN_GAP_MS: 30000, // at most one probe per tile per 30 s
    PROBE_ANSWER_MS: 10000, // no frame within 10 s of the probe -> stall
    NO_PICTURE_MS: 20000, // no first frame 20 s after scrcpy_initial -> "no picture"
    TOO_SLOW_DELAY_MS: 500, // close 1009 "viewer too slow" -> reconnect after 0.5 s
    TOO_SLOW_WINDOW_MS: 5 * 60000, // 1009s counted over 5 min (review should-fix 1)
    TOO_SLOW_FREE: 2, // the first two 1009s in the window are free; the third counts
    CLOSED_NOTE_GRACE_MS: 2000, // a bare `closed` within 2 s of open belongs to the previous socket (M1)
    NOTE_GRACE_MS: 10000, // a note with retryInMs holds the socket until arrival + retryInMs + 10 s
    SERVER_HOLD_MS: 75000, // UPLINK_OFFLINE/LOST/WAITING hold it 75 s (WAITING repeats every 60 s)
    DIRECT_INITIAL_MS: 8000, // AUTO: a direct socket with no scrcpy_initial in 8 s -> server route
    TICK_MS: 1000,
    // MF7 (2026-09-29, the owner's Pixel with its screen off): HOLD instead of reconnect (the page's `hold` switch).
    PC_QUIET_MAX_MS: 5 * 60000, // server route, a launcher-r4 PC: nothing heard and no picture for 5 min -> backed-off reopen
    PC_NO_PICTURE_MS: 40000, // server route, r4 PC: silence past the PC's own liveness check (20 s + 10 s) -> "no picture"
    HOLD_PROBE_GAPS_MS: [30000, 60000, 120000], // direct route, held: the next liveness checks (then every 120 s)
    DIRECT_HOLD_MAX_MS: 10 * 60000, // direct route, held with no picture for 10 min -> one backed-off reopen
});

/** PC codes that say the relay on the PC was down (the next OPEN/INITIAL means it started): the AUTO re-probe hint. */
const RELAY_DOWN_CODES = new Set(['CONNECT_REFUSED', 'HANDSHAKE_FAILED']);

const MAGIC_INITIAL = asciiBytes('scrcpy_initial'); // 14 bytes
const MAGIC_MESSAGE = asciiBytes('scrcpy_message'); // 14 bytes (same length)
const TYPE_CHANGE_STREAM_PARAMETERS = 101;

function asciiBytes(s) {
    const a = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) a[i] = s.charCodeAt(i) & 0xff;
    return a;
}

function startsWith(view, magic) {
    if (view.byteLength < magic.length) return false;
    for (let i = 0; i < magic.length; i++) if (view[i] !== magic[i]) return false;
    return true;
}

function toHex(v) {
    return v.toString(16).padStart(2, '0').toUpperCase();
}

/**
 * Serialize a VideoSettings quality tier into the exact 35-byte struct the fork
 * uses (VideoSettings.toBuffer): bitrate i32BE, maxFps i32BE, iFrameInterval i8,
 * bounds w/h i16BE, crop l/t/r/b i16BE, sendFrameMeta i8, lockedVideoOrientation
 * i8, displayId i32BE, codecOptions-len i32BE(=0), encoderName-len i32BE(=0).
 * We keep sendFrameMeta=0 so the wire is a plain Annex-B NAL stream.
 */
function videoSettingsStruct(q) {
    const b = new ArrayBuffer(35);
    const d = new DataView(b);
    let o = 0;
    d.setInt32(o, q.bitrate); o += 4;
    d.setInt32(o, q.maxFps); o += 4;
    d.setInt8(o, q.iFrameInterval ?? 10); o += 1;
    d.setInt16(o, q.maxSize); o += 2; // bounds width
    d.setInt16(o, q.maxSize); o += 2; // bounds height
    d.setInt16(o, 0); o += 2; // crop left
    d.setInt16(o, 0); o += 2; // crop top
    d.setInt16(o, 0); o += 2; // crop right
    d.setInt16(o, 0); o += 2; // crop bottom
    d.setInt8(o, 0); o += 1; // sendFrameMeta = false
    d.setInt8(o, -1); o += 1; // lockedVideoOrientation = -1 (unlocked)
    d.setInt32(o, 0); o += 4; // displayId = 0
    d.setInt32(o, 0); o += 4; // codecOptions length = 0
    d.setInt32(o, 0); o += 4; // encoderName length = 0
    return new Uint8Array(b);
}

/** Prefix the type byte (101) to the 35-byte struct -> the 36-byte control msg. */
function changeStreamParamsMessage(q) {
    const struct = videoSettingsStruct(q);
    const msg = new Uint8Array(1 + struct.length);
    msg[0] = TYPE_CHANGE_STREAM_PARAMETERS;
    msg.set(struct, 1);
    return msg;
}

/** Build the proxy-adb WebSocket URL for a device serial (udid). Quality is NOT
 *  in the URL (that was V2's mistake); it travels in the control message. LOCAL transport. */
export function buildStreamUrl(serial, base = WSSCRCPY_BASE) {
    return `${base}/?action=proxy-adb&remote=tcp:${SCRCPY_REMOTE_PORT}&udid=${encodeURIComponent(serial)}`;
}

/**
 * Build Spring's binary viewer WebSocket URL for an install's phone (REMOTE transport). The session
 * cookie rides the handshake because the dashboard and API are same-site, so there is NO ?token=.
 * The serial is fixed by the URL; the browser sends only the unchanged 36-byte control message.
 * http(s)://host/uiapi -> ws(s)://host/uiapi/bm/vview/{installId}?serial=..
 */
export function buildRemoteStreamUrl(installId, serial, apiBase = SPRING_API_BASE) {
    const raw = `${String(apiBase).replace(/\/+$/, '')}/bm/vview/${encodeURIComponent(installId)}`;
    const absolute = /^https?:\/\//i.test(raw)
        ? raw
        : `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
    const u = new URL(absolute);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    u.searchParams.set('serial', serial);
    return u.toString();
}

export function isWebCodecsSupported() {
    return typeof window !== 'undefined' && typeof window.VideoDecoder === 'function';
}

// Process-wide socket telemetry. Survives individual stream release, so callers
// (and the proof) can confirm that a page change actually closed old sockets.
export const streamTelemetry = { opens: 0, closes: 0 };

const nowMs = () => Date.now();
const NOTE_TYPES = new Set(['vstat', 'note']);

function defaultLog(line) {
    // eslint-disable-next-line no-console
    console.info(line);
}

function defaultVisible() {
    return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function fmtSec(ms) {
    const s = Math.max(0, ms) / 1000;
    return `${s < 10 ? s.toFixed(1) : Math.round(s)} s`;
}

function fmtMb(bytes) {
    return `${(Math.max(0, bytes) / 1048576).toFixed(1)} MB`;
}

/**
 * One live phone stream = one WebSocket + one WebCodecs decoder + one <canvas>.
 * Owns its own socket so it can be raised/lowered independently of siblings.
 *
 * SELF-HEAL (2026-09-29, docs/MONITOR-VIDEO-SELFHEAL-DESIGN.md V2/V3/V5). Everything below is OFF
 * unless the caller passes `heal: true` (the page's `reconnect` switch) - with it off this object is
 * today's one-shot socket: no retry, no timer, no probe, and no socket closed on a note.
 *   - every close resets `ws`, `started` and the decoder, so a reused object sends its quality
 *     settings again on the next socket (before, `started` stayed true and a second socket stayed
 *     gated = black);
 *   - the close code decides: give up (4401/4403/4404/4429/4503/1008 on the server route), 0.5 s
 *     (1009 "viewer too slow"), or bounded backoff 1,2,4,8,15,15,15,15 s then 60 s x30 then Paused;
 *     every delay +-40% jitter; a first frame resets the counters;
 *   - stall checks: not open in 10 s; open without scrcpy_initial or a note in 20 s; and - because a
 *     static or locked screen sends no frames - a liveness probe (the one allowed upstream frame, the
 *     36-byte settings, which forces an SPS + IDR) before any "no picture" verdict;
 *   - text frames {"t":"vstat",...} from the server set the tile's words; only a LIVE note may close
 *     the socket (never `replay:true`, never a bare `closed` within 2 s of open - review M1);
 *   - AUTO route (`autoFallback`): a direct socket that fails - before its scrcpy_initial (a phone on
 *     another PC) or later (the relay here stopped: with the owner's D6 LEAN the client stops an idle
 *     relay, and it cannot see a browser streaming direct) - moves THIS phone to the server route, once
 *     and until Refresh re-probes. The server route makes the PC subscribe, which starts the relay again.
 *     Not counted as a retry.
 *
 * HOLD (MF7, 2026-09-29, `hold: true` - the page's `hold` switch). On the owner's Pixel the pages reopened about every 30 s
 * while the phone's screen was off: a screen that is off sends nothing, so every liveness check went unanswered, and each
 * page closed and reopened its socket - through the server that is an unsubscribe + subscribe, which also reset the PC's
 * own backoff, and on the direct route the tile even moved to the server. With `hold` on:
 *   - server route, a launcher-r4 PC (it names its reasons in coded notes): the PC owns the phone's liveness (its own probe,
 *     STALLED, retry, FLAPPING, SCREEN_OFF, GAVE_UP). The page NEVER closes the socket for silence or for a PC note; it
 *     shows the PC's words, or "no picture" after PC_NO_PICTURE_MS. GAVE_UP still ends the tile (Retry / Repair). A
 *     backed-off reopen happens only when nothing reports anything: no note and no picture for PC_QUIET_MAX_MS.
 *   - direct route (the relay on this PC keeps the phone's stream open; a dead phone-side stream closes the socket): an
 *     unanswered liveness check holds the socket in "no picture", and the next checks back off (30, 60, 120 s). A socket we
 *     close ourselves after a stream started never moves the tile to the server route. After DIRECT_HOLD_MAX_MS without a
 *     picture: one backed-off reopen (nothing on the direct route can report a reason).
 *   - a PC that sends only bare states (launcher r3) keeps today's probe / close / backoff: someone has to heal it.
 * The picture comes back on the SAME socket when the screen is turned on.
 */
export class ScrcpyStream {
    /**
     * @param {object} opts
     * @param {string} opts.serial   device udid (adb -s). Only ever RZCT91M5BWT in test.
     * @param {HTMLCanvasElement} opts.canvas  target canvas
     * @param {'low'|'high'} [opts.quality='low']  initial tier
     * @param {string} [opts.base]  ws base origin (LOCAL transport)
     * @param {'local'|'remote'} [opts.transport='local']  where to reach the phone
     * @param {number|string} [opts.installId]  install id (REMOTE transport)
     * @param {string} [opts.apiBase]  Spring API base (REMOTE transport)
     * @param {(s:object)=>void} [opts.onStatus]  event callback (open/close/error/initial/settings)
     * @param {boolean} [opts.heal=false]  self-heal on (the page's `reconnect` switch)
     * @param {boolean} [opts.autoFallback=false]  AUTO route: direct first, server route on failure
     * @param {object} [opts.remoteLow]  the server-route grid tier (default QUALITY_REMOTE_LOW)
     * @param {object} [opts.remoteHigh]  the server-route expanded tier (default QUALITY.high)
     * @param {string} [opts.label]  the PC's name, for logs and tile words
     * @param {(status:object)=>void} [opts.onState]  honest tile state, on every change
     * @param {(stream:ScrcpyStream)=>void} [opts.onAuthSuspect]  two server-route closes before open in a row
     * @param {()=>number} [opts.rng]  random source for jitter (tests pass a seeded one)
     * @param {()=>boolean} [opts.isVisible]  tab visibility (slow-phase retries wait while hidden)
     * @param {(line:string)=>void} [opts.log]  log sink (console.info by default)
     */
    constructor(opts) {
        this.serial = opts.serial;
        this.canvas = opts.canvas;
        this.base = opts.base || WSSCRCPY_BASE;
        this.quality = opts.quality || 'low';
        this.onStatus = opts.onStatus || (() => {});

        // Transport target. 'local' is byte-for-byte the proven on-PC path; 'remote' points the SAME
        // decoder at Spring's viewer WebSocket, which relays byte-identical scrcpy bytes.
        this.transport = opts.transport === 'remote' ? 'remote' : 'local';
        this.installId = opts.installId;
        this.apiBase = opts.apiBase || SPRING_API_BASE;
        // Optional explicit ws URL; see connect(). Used where the URL must not
        // contain a serial or udid.
        this.url = opts.url || null;

        // Self-heal (see the class comment). Off unless asked for.
        this.heal = !!opts.heal;
        this.autoFallback = !!opts.autoFallback && this.installId != null && !this.url;
        this.remoteLow = opts.remoteLow || QUALITY_REMOTE_LOW;
        this.remoteHigh = opts.remoteHigh || null;
        this.label = opts.label || '';
        this.onState = typeof opts.onState === 'function' ? opts.onState : null;
        this.onAuthSuspect = typeof opts.onAuthSuspect === 'function' ? opts.onAuthSuspect : null;
        // MF7: hold a silent socket instead of reconnecting (see the class comment), and hints for the page's re-probe.
        this.hold = !!opts.hold;
        this.onHint = typeof opts.onHint === 'function' ? opts.onHint : null;
        this.logTag = opts.logTag ? String(opts.logTag) : '';
        this.rng = typeof opts.rng === 'function' ? opts.rng : Math.random;
        this.isVisible = typeof opts.isVisible === 'function' ? opts.isVisible : defaultVisible;
        this.log = typeof opts.log === 'function' ? opts.log : defaultLog;

        this.ws = null;
        this.decoder = null;
        this.ctx = this.canvas ? this.canvas.getContext('2d') : null;

        // decode state (mirrors the fork's WebCodecsPlayer buffering)
        this._buffer = null;
        this._configured = false;
        this._hadIDR = false;
        this._ts = 0;

        // instrumentation (the proof reads these)
        this.videoBytes = 0; // cumulative video (NAL) bytes only
        this.totalBytes = 0;
        this.settingsSent = 0; // count of TYPE_CHANGE_STREAM_PARAMETERS sent
        this.opens = 0;
        this.closes = 0;
        this.frames = 0; // decoded frames drawn
        this.started = false; // stream started (first settings sent) ON THE CURRENT SOCKET
        this._stopped = false;
        this._lastError = null;
        this.lastFrameAt = 0;
        this.firstFrameMs = null;

        // self-heal state
        this._sock = null; // the current socket's record (timestamps, counters, last note)
        this._attempt = 0; // retries since the last picture (fast phase index)
        this._slowTries = 0;
        this._tooSlow = []; // times of 1009 closes, 5-min window
        this._preOpenFails = 0; // server-route closes before open, in a row (signed-out check)
        this._fellBack = false; // AUTO: already moved to the server route
        this._everInitial = false; // any socket of this object reached scrcpy_initial
        this._lastProbeAt = 0;
        this._timers = { retry: null, start: null, tick: null };
        this.status = Object.freeze({
            state: 'idle', route: this._route(), reason: null, closeCode: null, note: null, attempt: 0,
            phase: null, slowTries: 0, nextAt: null, gaveUp: null, unchanged: false, scheduled: false,
            everPicture: false, since: nowMs(), serial: this.serial, installId: this.installId,
        });
    }

    /** 'remote' (through Spring), 'local' (direct to the relay on this PC) or 'custom' (explicit url). */
    _route() {
        if (this.url) return 'custom';
        return this.transport === 'remote' && this.installId != null ? 'remote' : 'local';
    }

    connect() {
        if (this.ws || this._stopped) return;
        this._clearTimer('start');
        this._clearTimer('retry');
        // Only the TARGET differs between transports; the wire (scrcpy_initial gate, raw Annex-B NALs,
        // the 36-byte control message) is identical because Spring relays opaque scrcpy bytes.
        // An explicit URL wins over both builders.
        //
        // Added for the CloudPhones product, where BOTH builders are unusable:
        // buildStreamUrl embeds `udid=<serial>` and buildRemoteStreamUrl embeds
        // `?serial=<serial>`, so either one hands a customer the handset serial
        // in devtools. That product requires an opaque, per-session URL
        // (`/api/session/stream?s=<token>`). Nothing here changes for existing
        // callers, which pass no `url` and keep the old behaviour.
        const url = this.url
            ?? ((this.transport === 'remote' && this.installId != null)
                ? buildRemoteStreamUrl(this.installId, this.serial, this.apiBase)
                : buildStreamUrl(this.serial, this.base));
        const sock = {
            ws: null, createdAt: nowMs(), openAt: 0, initialAt: 0, settingsAt: 0, firstFrameAt: 0,
            lastFrameAt: 0, probeAt: 0, probePending: false, frames: 0, bytes: 0, note: null, noteDeadline: 0, errorTimer: null,
            uplinkDown: false,
            // MF7 hold: the PC names its reasons (launcher r4), when it last spoke, unanswered checks while held,
            // and whether the PC said its relay was down (the next OPEN/INITIAL is the re-probe hint).
            pcHeals: false, lastNoteAt: 0, heldProbes: 0, relayWasDown: false,
        };
        let ws;
        try {
            ws = new WebSocket(url);
        } catch (e) {
            this._lastError = 'open: ' + (e && e.message ? e.message : e);
            this._afterClose(sock, { code: 1006, reason: this._lastError });
            return;
        }
        ws.binaryType = 'arraybuffer';
        sock.ws = ws;
        this.ws = ws;
        this._sock = sock;
        this._setState('connecting', { note: null });
        this._log(`connecting (try ${this._attempt + 1})`);
        ws.onopen = () => {
            this.opens++;
            streamTelemetry.opens++;
            if (this.ws !== ws) return;
            sock.openAt = nowMs();
            this._preOpenFails = 0;
            this._emit('open');
            this._log(`open ${sock.openAt - sock.createdAt} ms`);
            this._setState('waiting');
        };
        ws.onclose = (ev) => {
            this.closes++;
            streamTelemetry.closes++;
            if (sock.errorTimer) clearTimeout(sock.errorTimer);
            if (this.ws !== ws) return; // stopped, or a socket we already closed ourselves
            this._emit('close');
            this._afterClose(sock, ev);
        };
        ws.onerror = () => {
            if (this.ws !== ws) return;
            this._emit('error');
            // A browser follows a failed handshake's error with close 1006 at once. Some engines (Node's
            // undici, seen 2026-09-29 on a refused localhost port) fire only the error. With self-heal or
            // AUTO on, an error before open with no close within 1.5 s is handled as that close.
            if (!sock.openAt && (this.heal || this.autoFallback) && !sock.errorTimer) {
                sock.errorTimer = setTimeout(() => {
                    sock.errorTimer = null;
                    if (this.ws !== ws || sock.openAt) return;
                    this.ws = null;
                    try {
                        ws.close();
                    } catch (e) {
                        /* noop */
                    }
                    this._afterClose(sock, { code: 1006, reason: 'error before open (no close event)' });
                }, 1500);
            }
        };
        ws.onmessage = (ev) => {
            if (this.ws !== ws) return;
            this._onMessage(ev.data);
        };
        if (this.heal || this.autoFallback) this._startTick();
    }

    /** Open after `ms` (the page staggers first opens 0-1 s so a page never dials every tile at once). */
    connectAfter(ms) {
        if (this.ws || this._stopped) return;
        if (!(ms > 0)) {
            this.connect();
            return;
        }
        this._clearTimer('start');
        this._setState('connecting', { scheduled: true });
        this._timers.start = setTimeout(() => {
            this._timers.start = null;
            this.connect();
        }, ms);
    }

    _onMessage(data) {
        if (typeof data === 'string') {
            this._onText(data);
            return;
        }
        if (!(data instanceof ArrayBuffer)) return;
        this.totalBytes += data.byteLength;
        const s = this._sock;
        if (s) s.bytes += data.byteLength;
        const view = new Uint8Array(data);
        if (data.byteLength > MAGIC_INITIAL.length) {
            if (startsWith(view, MAGIC_INITIAL)) {
                if (s && !s.initialAt) {
                    s.initialAt = nowMs();
                    this._log(`initial ${fmtSec(s.initialAt - s.createdAt)}`);
                } else if (s && this.heal) {
                    // A SECOND scrcpy_initial on the same socket is a new phone-side stream (the server
                    // re-subscribed after UPLINK_BACK, or the PC retried the phone). It is gated like the
                    // first one, so it needs the settings again - or it stays black.
                    s.probePending = false;
                    this._log('initial again (the phone-side stream restarted) - sending the settings again');
                }
                this._everInitial = true;
                // Stream is gated behind the first settings message: send our tier
                // now, which is what actually starts the video flowing.
                if (!this.started || (this.heal && s)) this._sendSettings();
                this._emit('initial');
                const st = this.status.state;
                if (st === 'connecting' || st === 'waiting') this._setState('starting');
                return;
            }
            if (startsWith(view, MAGIC_MESSAGE)) {
                return; // device message (clipboard) - not needed here
            }
        }
        // raw H.264 NAL unit
        this.videoBytes += data.byteLength;
        this._decode(view);
    }

    /**
     * A text frame from the server (design V5 / S1 / S2): {"t":"vstat", serial, state, code, reason,
     * attempt, retryInMs, src, replay}. Malformed or foreign frames are ignored; an old server sends
     * none, so nothing changes there.
     */
    _onText(text) {
        if (!text || text.length > 8192) return;
        let m;
        try {
            m = JSON.parse(text);
        } catch (e) {
            return;
        }
        if (!m || typeof m !== 'object' || !NOTE_TYPES.has(m.t)) return;
        if (m.serial != null && m.serial !== '' && String(m.serial) !== String(this.serial)) return;
        const s = this._sock;
        if (!s) return;
        const t = nowMs();
        const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
        const note = Object.freeze({
            state: m.state != null ? String(m.state).slice(0, 32) : '',
            code: m.code != null ? String(m.code).slice(0, 48) : '',
            // eslint-disable-next-line no-control-regex
            reason: m.reason != null ? String(m.reason).replace(/[\u0000-\u001f\u007f]+/g, ' ').slice(0, 300) : '',
            retryInMs: num(m.retryInMs),
            attempt: num(m.attempt),
            src: m.src != null ? String(m.src).slice(0, 16) : '',
            replay: m.replay === true,
            at: t,
        });
        s.note = note;
        s.lastNoteAt = t;
        this._log(`server note ${note.code || note.state || '?'}${note.reason ? ` "${note.reason}"` : ''}`
            + `${note.retryInMs != null ? ` - PC retries in ${fmtSec(note.retryInMs)}` : ''}${note.replay ? ' (replayed)' : ''}`);
        this._setState(this.status.state, { note });
        const code = note.code.toUpperCase();
        const serverNote = note.src === 'server' || SERVER_NOTE_CODES.has(code);
        if (code && !serverNote && !s.pcHeals) {
            // A coded note from the PC (a replayed one counts): a launcher-r4 PC, which heals this phone itself.
            s.pcHeals = true;
            if (this.hold) this._log(`${this.label || 'the PC'} heals this phone itself (its notes name the reason) - this page holds its socket through silence`);
        }

        // Review M1: only a LIVE note may act. A replayed one only sets the words.
        if (!this.heal || note.replay) return;
        const st = note.state.toLowerCase();
        // MF7 re-probe hint: the PC said its relay was down, and now it opened the phone - the relay on that PC started.
        if (RELAY_DOWN_CODES.has(code) || code.startsWith('PHLOGS_')) {
            s.relayWasDown = true;
        } else if (s.relayWasDown && (code === 'OPEN' || code === 'INITIAL')) {
            s.relayWasDown = false;
            this._hint('relay-started', code);
        }
        if (code === 'GAVE_UP') {
            this._giveUp('pc', noteText(note, this.label), null, 'the PC gave up');
            return;
        }
        if (this.hold && s.pcHeals && code && !serverNote) {
            this._holdOnNote(s, note, code, st, t);
            return;
        }
        if (serverNote) {
            // Spring's own notes (S2). Their retryInMs is when the server speaks again, NOT a PC retry.
            const hold = t + Math.max((note.retryInMs || 0) + HEAL.NOTE_GRACE_MS, HEAL.SERVER_HOLD_MS);
            if (code === 'UPLINK_OFFLINE' || code === 'UPLINK_LOST') {
                // The PC's video uplink is down; nothing to retry from here. The server re-subscribes
                // (and says UPLINK_BACK) when it returns, and repeats WAITING every 60 s meanwhile.
                s.uplinkDown = true;
                s.noteDeadline = hold;
            } else if (code === 'WAITING') {
                // A keep-alive. It holds the socket only while the uplink is down; otherwise our own
                // stall checks decide, so a stuck phone stream is still closed and retried.
                if (s.uplinkDown) s.noteDeadline = hold;
            } else if (code === 'UPLINK_BACK') {
                // A fresh subscribe went out: the phone gets the usual 20 s to start.
                s.uplinkDown = false;
                s.noteDeadline = t + HEAL.INITIAL_TIMEOUT_MS;
            }
            return;
        }
        if (note.retryInMs != null && note.retryInMs >= 0) {
            // The PC is retrying this phone (launcher r4): keep the socket through its window.
            s.noteDeadline = t + note.retryInMs + HEAL.NOTE_GRACE_MS;
            return;
        }
        if (st === 'closed' && t - (s.openAt || t) < HEAL.CLOSED_NOTE_GRACE_MS) {
            // r3 sends a bare `closed` for every unsubscribe; this one belongs to the previous socket.
            return;
        }
        if (st === 'error' || st === 'closed') {
            // The PC will not retry (launcher r3, or a cause nobody heals): our own backoff becomes the
            // PC's retry, because every reconnect makes Spring send a fresh subscribe.
            this._closeForRetry(noteText(note, this.label));
            return;
        }
        // Any other live note (open, INITIAL, UPLINK_BACK, ...) restarts the 20 s wait.
        s.noteDeadline = Math.max(s.noteDeadline || 0, t + HEAL.INITIAL_TIMEOUT_MS);
    }

    /**
     * MF7 hold: a live note from a PC that heals the phone itself. Never a close: the words only (a reconnect would unsubscribe
     * and resubscribe the phone, which resets the PC's own backoff and relaunches the phone-side stream).
     */
    _holdOnNote(s, note, code, st, t) {
        const says = code === 'SCREEN_OFF' || st === 'error' || st === 'closed';
        if (!says) return;
        const words = noteText(note, this.label);
        // The PC says so only after its own silence window, so its word wins over the last frame; the next frame (if any)
        // turns the tile back to "streaming".
        if (s.initialAt) {
            this._setState('no-picture', { reason: words, silentSince: s.lastFrameAt || s.initialAt, held: true });
        }
        this._log(`holding the socket through ${code}${note.retryInMs != null ? ` (the PC retries in ${fmtSec(note.retryInMs)})` : ''}`
            + ` - no reconnect from this page`);
    }

    /** A hint for the page's AUTO re-probe ('relay-started', 'first-frame'). Never throws. */
    _hint(kind, detail) {
        if (!this.onHint) return;
        try {
            this.onHint(kind, detail);
        } catch (e) {
            /* the page's problem, not the stream's */
        }
    }

    _ensureDecoder() {
        if (this.decoder && this.decoder.state !== 'closed') return;
        this.decoder = new VideoDecoder({
            output: (frame) => this._draw(frame),
            error: (e) => {
                this._lastError = String(e && e.message ? e.message : e);
                // reset decode state; next SPS/IDR re-primes the pipeline
                this._configured = false;
                this._hadIDR = false;
                this._buffer = null;
            },
        });
        this._configured = false;
    }

    // Mirrors the fork's WebCodecsPlayer.decode(): each message is one NAL unit
    // (or the bundled SPS+PPS). Accumulate config, decode on IDR/P as Annex-B.
    _decode(nal) {
        if (!nal || nal.length < 5) return;
        this._ensureDecoder();
        const type = nal[4] & 0x1f; // nal[0..3] = 00 00 00 01 start code

        if (type === 7) {
            // SPS (may be bundled with PPS in the same message). Configure decoder
            // from profile/constraint/level, then start a fresh access unit.
            const codec = `avc1.${toHex(nal[5])}${toHex(nal[6])}${toHex(nal[7])}`;
            try {
                this.decoder.configure({ codec, optimizeForLatency: true });
                this._configured = true;
            } catch (e) {
                this._lastError = 'configure: ' + e;
                this._configured = false;
            }
            this._buffer = nal.slice();
            this._hadIDR = false;
            return;
        }
        if (type === 8) {
            // PPS arriving separately -> append to the config buffer.
            this._buffer = concatU8(this._buffer, nal);
            return;
        }
        if (type === 6 && !this._configured) {
            return; // lonely SEI before config
        }

        this._buffer = concatU8(this._buffer, nal);
        if (type === 5) this._hadIDR = true;

        if (this._buffer && this._configured && this.decoder.state === 'configured' && this._hadIDR) {
            const chunkData = this._buffer;
            this._buffer = null;
            try {
                this.decoder.decode(
                    new EncodedVideoChunk({
                        type: this._hadIDR && type === 5 ? 'key' : 'delta',
                        timestamp: this._ts++,
                        data: chunkData,
                    }),
                );
            } catch (e) {
                this._lastError = 'decode: ' + e;
                this._configured = false;
                this._hadIDR = false;
            }
        }
    }

    _draw(frame) {
        this.frames++;
        const s = this._sock;
        if (s) {
            const t = nowMs();
            s.frames++;
            if (!s.firstFrameAt) {
                s.firstFrameAt = t;
                this.firstFrameMs = t - s.createdAt;
                this._log(`first frame ${fmtSec(this.firstFrameMs)}`);
                this._hint('first-frame', this.firstFrameMs);
            }
            if (this.status.state === 'no-picture') {
                this._log(`picture back after ${fmtSec(t - (this.status.silentSince || s.lastFrameAt || s.initialAt || t))} on the same socket`
                    + `${s.heldProbes ? ` (${s.heldProbes} unanswered liveness check(s) held, no reconnect)` : ''}`);
            }
            s.heldProbes = 0;
            s.lastFrameAt = t;
            s.probePending = false; // a frame answers any liveness check
            this.lastFrameAt = t;
            // A picture resets the retry budget (design V2).
            this._attempt = 0;
            this._slowTries = 0;
            this._preOpenFails = 0;
            if (this.status.state !== 'streaming' || this.status.unchanged || !this.status.everPicture) {
                this._setState('streaming', { unchanged: false, everPicture: true });
            }
        }
        if (!this._stopped && this.ctx) {
            const w = frame.displayWidth || frame.codedWidth;
            const h = frame.displayHeight || frame.codedHeight;
            if (this.canvas.width !== w || this.canvas.height !== h) {
                this.canvas.width = w;
                this.canvas.height = h;
            }
            try {
                this.ctx.drawImage(frame, 0, 0);
            } catch (e) {
                /* frame closed race - ignore */
            }
        }
        frame.close();
    }

    /** The tier to send now: HIGH when expanded (every route), else the route's LOW. */
    _tier() {
        const remote = this._route() === 'remote';
        if (this.quality === 'sharp') return (remote && (this.remoteHigh || QUALITY.high)) || QUALITY.sharp;
        if (this.quality === 'high') return (remote && this.remoteHigh) || QUALITY.high;
        return remote ? this.remoteLow : QUALITY.low;
    }

    _sendSettings() {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        this.ws.send(changeStreamParamsMessage(this._tier()));
        this.settingsSent++;
        this.started = true;
        if (this._sock) this._sock.settingsAt = nowMs();
        this._emit('settings');
    }

    /**
     * Raise/lower THIS stream in place on its existing socket. No reconnect: the
     * scrcpy server re-encodes at the new bitrate on receipt of the message.
     * @param {'low'|'high'} quality
     */
    setQuality(quality) {
        if (quality !== 'low' && quality !== 'high' && quality !== 'sharp') return;
        if (this.quality === quality && this.started) return;
        this.quality = quality;
        if (this.started) this._sendSettings();
    }

    // ---------------------------------------------------------------- self-heal

    _clearTimer(name) {
        const t = this._timers[name];
        if (!t) return;
        if (name === 'tick') clearInterval(t);
        else clearTimeout(t);
        this._timers[name] = null;
    }

    _startTick() {
        this._clearTimer('tick');
        this._timers.tick = setInterval(() => this._tick(), HEAL.TICK_MS);
    }

    _jitter(ms) {
        const r = Math.min(1, Math.max(0, Number(this.rng()) || 0));
        return Math.max(0, Math.round(ms * (1 - HEAL.JITTER + 2 * HEAL.JITTER * r)));
    }

    _canFallBack() {
        return this.autoFallback && !this._fellBack && this.transport === 'local';
    }

    /** Close the current socket ourselves (a stall, a note) and continue as after any close. */
    _closeForRetry(why) {
        const ws = this.ws;
        const sock = this._sock;
        if (!ws) return;
        this.ws = null; // the socket's own late onclose now only counts telemetry
        try {
            ws.close();
        } catch (e) {
            /* noop */
        }
        this._afterClose(sock, { code: 0, reason: why, byUs: true });
    }

    _afterClose(sock, ev) {
        const byUs = !!(ev && ev.byUs);
        const code = ev && typeof ev.code === 'number' ? ev.code : 1006;
        const reason = (ev && ev.reason) || '';
        this._clearTimer('tick');
        this.ws = null;
        this._sock = null;
        // The fix for the second-socket black tile: `started` is per SOCKET. Leaving it true meant the
        // next socket never sent its settings on scrcpy_initial and stayed gated.
        this.started = false;
        this._resetDecoder();
        if (this._stopped) return;
        const t = nowMs();
        const opened = !!(sock && sock.openAt);
        const route = this._route();
        const summary = `${byUs ? `closed by the page (${reason})` : `closed ${code}${reason ? ` "${reason}"` : ' ""'}`}`
            + ` after ${fmtSec(t - (sock ? sock.openAt || sock.createdAt : t))}, ${sock ? sock.frames : 0} frames, ${fmtMb(sock ? sock.bytes : 0)}`;

        // AUTO: the direct route failed (a phone on another PC, or the relay here stopped) -> the server route.
        // MF7 hold: a socket WE closed after its stream started (the relay was alive) is not a reason to leave the relay.
        if (this._canFallBack() && !(this.hold && byUs && sock && sock.initialAt)) {
            this._fallBackToRemote(summary);
            return;
        }
        if (route === 'remote' && !opened && !byUs) {
            // A signed-out browser gets HTTP 401 at the handshake, which the browser reports as 1006
            // before open. Two in a row: the page makes one authenticated call to find out.
            this._preOpenFails++;
            if (this._preOpenFails >= 2 && this.onAuthSuspect) {
                try {
                    this.onAuthSuspect(this);
                } catch (e) {
                    /* the page's problem, not the stream's */
                }
            }
        }
        const why = byUs ? reason : closeText(code, reason, route);
        if (!this.heal) {
            this._log(`${summary}; reconnect is off`);
            this._setState('paused', { reason: why, closeCode: byUs ? null : code, gaveUp: 'closed' });
            return;
        }
        const g = route === 'remote' && !byUs ? GIVE_UP_CLOSE[code] : null;
        if (g) {
            this._giveUp(g.kind, g.text, code, summary);
            return;
        }
        if (code === 1009 && !byUs) {
            this._tooSlow = this._tooSlow.filter((x) => t - x < HEAL.TOO_SLOW_WINDOW_MS);
            this._tooSlow.push(t);
            if (this._tooSlow.length <= HEAL.TOO_SLOW_FREE) {
                const delay = this._jitter(HEAL.TOO_SLOW_DELAY_MS);
                this._log(`${summary}; too slow - reconnect in ${fmtSec(delay)} (not counted)`);
                this._setState('retrying', {
                    reason: why, closeCode: code, attempt: this._attempt, phase: 'fast', nextAt: t + delay,
                });
                this._timers.retry = setTimeout(() => {
                    this._timers.retry = null;
                    this.connect();
                }, delay);
                return;
            }
            this._scheduleRetry('Your connection is too slow for this many live screens - show fewer per page', code, summary);
            return;
        }
        this._scheduleRetry(why, byUs ? null : code, summary);
    }

    _scheduleRetry(why, code, summary) {
        let delay;
        let phase;
        if (this._attempt < HEAL.FAST_DELAYS_MS.length) {
            delay = HEAL.FAST_DELAYS_MS[this._attempt];
            phase = 'fast';
        } else if (this._slowTries < HEAL.SLOW_TRIES) {
            delay = HEAL.SLOW_DELAY_MS;
            phase = 'slow';
            this._slowTries++;
        } else {
            this._giveUp('budget', why, code, summary);
            return;
        }
        this._attempt++;
        delay = this._jitter(delay);
        const nextAt = nowMs() + delay;
        const count = phase === 'fast'
            ? `${this._attempt}/${HEAL.FAST_DELAYS_MS.length}`
            : `slow ${this._slowTries}/${HEAL.SLOW_TRIES}`;
        this._log(`${summary}; retry in ${fmtSec(delay)} (${count})`);
        this._setState('retrying', {
            reason: why, closeCode: code, attempt: this._attempt, phase, slowTries: this._slowTries, nextAt,
        });
        this._timers.retry = setTimeout(() => this._retryDue(phase), delay);
    }

    _retryDue(phase) {
        this._timers.retry = null;
        if (this._stopped) return;
        if (phase === 'slow' && !this.isVisible()) {
            // The slow phase runs only while someone can see the tab; wait without spending a try.
            const nextAt = nowMs() + HEAL.SLOW_DELAY_MS;
            this._setState('retrying', { nextAt });
            this._timers.retry = setTimeout(() => this._retryDue(phase), HEAL.SLOW_DELAY_MS);
            return;
        }
        this.connect();
    }

    _fallBackToRemote(summary) {
        this._fellBack = true;
        this.transport = 'remote';
        const delay = Math.round(Math.min(1, Math.max(0, Number(this.rng()) || 0)) * 1000);
        this._log(`${summary}; the direct route does not reach this phone - switching to the server route in ${fmtSec(delay)}`);
        this._setState('connecting', { reason: 'Not on this PC - trying through the server', scheduled: true });
        this._timers.start = setTimeout(() => {
            this._timers.start = null;
            this.connect();
        }, delay);
    }

    _giveUp(kind, text, code, summary) {
        this._clearTimer('retry');
        this._clearTimer('start');
        this._clearTimer('tick');
        if (this.ws) {
            const ws = this.ws;
            this.ws = null;
            try {
                ws.close();
            } catch (e) {
                /* noop */
            }
            this._sock = null;
            this.started = false;
            this._resetDecoder();
        }
        this._log(`${summary ? `${summary}; ` : ''}gave up (${kind}): ${text}`);
        this._setState('paused', { reason: text, closeCode: code == null ? this.status.closeCode : code, gaveUp: kind });
    }

    /** Stop this tile with a reason (signed out, idle pause); Retry reopens it. */
    giveUp(kind, text) {
        if (this._stopped) return;
        this._giveUp(kind, text, null, null);
    }

    /** The Retry button: fresh budget, reconnect now. */
    retry() {
        if (this._stopped) return;
        this._clearTimer('retry');
        this._clearTimer('start');
        this._attempt = 0;
        this._slowTries = 0;
        this._tooSlow = [];
        this._preOpenFails = 0;
        if (this.ws) {
            const ws = this.ws;
            this.ws = null;
            try {
                ws.close();
            } catch (e) {
                /* noop */
            }
            this._sock = null;
            this.started = false;
            this._resetDecoder();
        }
        this._log('retry pressed');
        this.connect();
    }

    _sendProbe(s, ref) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        const t = nowMs();
        // Review M3: the ONE upstream frame the relay allows (anything else closes 1008). The scrcpy
        // server re-encodes in place and emits a fresh SPS + IDR, so a healthy link answers with a frame
        // even when the screen is static or locked.
        this.ws.send(changeStreamParamsMessage(this._tier()));
        this.settingsSent++;
        s.probeAt = t;
        s.probePending = true; // cleared by the next decoded frame
        this._lastProbeAt = t;
        this._log(`no frame for ${fmtSec(t - ref)} - liveness check (settings re-sent)`);
    }

    _tick() {
        const s = this._sock;
        if (!s || this._stopped || this.ws !== s.ws) return;
        const t = nowMs();
        if (!s.initialAt && this._canFallBack() && t - s.createdAt >= HEAL.DIRECT_INITIAL_MS) {
            this._closeForRetry('no stream on the direct route in 8 s');
            return;
        }
        if (!this.heal) return;
        if (this.hold && s.openAt && s.pcHeals && this._route() === 'remote') {
            this._tickPcHeals(s, t);
            return;
        }
        if (!s.openAt) {
            if (t - s.createdAt >= HEAL.OPEN_TIMEOUT_MS) this._closeForRetry('the connection did not open in 10 s');
            return;
        }
        if (!s.initialAt) {
            const deadline = Math.max(s.openAt + HEAL.INITIAL_TIMEOUT_MS, s.noteDeadline || 0);
            if (t >= deadline) {
                this._closeForRetry(s.note ? `still no stream - ${noteText(s.note, this.label)}` : 'no stream from the phone in 20 s');
            }
            return;
        }
        // The silence clock restarts at the last frame or the last settings sent (a new initial, a quality change).
        const ref = Math.max(s.lastFrameAt || 0, s.settingsAt || 0, s.initialAt || 0);
        if (this.hold && this._route() !== 'remote') {
            this._tickDirectHold(s, t, ref);
            return;
        }
        const probePending = s.probePending;
        if (probePending && t - s.probeAt >= HEAL.PROBE_ANSWER_MS) {
            if (s.noteDeadline && t < s.noteDeadline) {
                // The PC is already retrying (a note with retryInMs): closing would only churn.
                if (this.status.state !== 'no-picture') {
                    this._setState('no-picture', { reason: noteText(s.note, this.label) || null, silentSince: ref });
                }
                return;
            }
            this._closeForRetry(s.lastFrameAt ? 'no new picture after a liveness check' : 'the phone sent no picture');
            return;
        }
        if (!probePending && t - ref >= HEAL.PROBE_AFTER_MS && t - this._lastProbeAt >= HEAL.PROBE_MIN_GAP_MS) {
            this._sendProbe(s, ref);
        }
        if (!s.lastFrameAt) {
            if (t - s.initialAt >= HEAL.NO_PICTURE_MS && this.status.state !== 'no-picture') {
                this._setState('no-picture', { reason: 'No picture from the phone yet', silentSince: s.initialAt });
            }
            return;
        }
        if (t - s.lastFrameAt >= HEAL.PROBE_AFTER_MS && this.status.state === 'streaming' && !this.status.unchanged) {
            this._setState('streaming', { unchanged: true });
        }
    }

    /**
     * MF7 hold, server route, a launcher-r4 PC: the PC probes the phone, retries it and says why. This page sends no liveness
     * check of its own and never closes the socket for silence; it only draws the state. One exception: nothing heard from
     * the PC and no picture for PC_QUIET_MAX_MS - then nothing reports the phone, and a backed-off reopen is allowed.
     */
    _tickPcHeals(s, t) {
        const heard = Math.max(s.lastNoteAt || 0, s.lastFrameAt || 0, s.initialAt || 0, s.openAt || 0);
        if (t - heard >= HEAL.PC_QUIET_MAX_MS) {
            this._closeForRetry(`no word from ${this.label || 'the PC'} and no picture for ${fmtSec(t - heard)}`);
            return;
        }
        if (!s.initialAt) return; // waiting for the phone: the PC's notes are the words
        const since = s.lastFrameAt || s.initialAt;
        const silent = t - since;
        if (s.lastFrameAt && silent >= HEAL.PROBE_AFTER_MS && silent < HEAL.PC_NO_PICTURE_MS) {
            if (this.status.state === 'streaming' && !this.status.unchanged) this._setState('streaming', { unchanged: true });
            return;
        }
        const limit = s.lastFrameAt ? HEAL.PC_NO_PICTURE_MS : HEAL.NO_PICTURE_MS;
        if (silent >= limit && this.status.state !== 'no-picture') {
            const n = s.note && !s.note.replay ? s.note : null;
            const informative = n && (n.code.toUpperCase() === 'SCREEN_OFF' || n.state.toLowerCase() === 'error');
            const words = informative ? noteText(n, this.label)
                : `No new picture - the phone's screen may be off or locked; ${this.label || 'the PC'} keeps checking it`;
            this._log(`no picture for ${fmtSec(silent)} - holding the socket (${this.label || 'the PC'} checks the phone itself)`);
            this._setState('no-picture', { reason: words, silentSince: since, held: true });
        }
    }

    /**
     * MF7 hold, direct route: the relay on this PC keeps the phone's stream open (a dead phone-side stream closes this socket),
     * so silence is a screen that is off, locked or static. A liveness check that gets no picture HOLDS the socket; the next
     * checks back off (HOLD_PROBE_GAPS_MS). After DIRECT_HOLD_MAX_MS without a picture: one backed-off reopen.
     */
    _tickDirectHold(s, t, ref) {
        const since = s.lastFrameAt || s.initialAt;
        if (s.probePending && t - s.probeAt >= HEAL.PROBE_ANSWER_MS) {
            s.probePending = false;
            s.heldProbes++;
            const gaps = HEAL.HOLD_PROBE_GAPS_MS;
            const next = gaps[Math.min(s.heldProbes - 1, gaps.length - 1)];
            this._log(`the liveness check got no picture in ${fmtSec(t - s.probeAt)} - holding the socket`
                + ` (unanswered ${s.heldProbes}x); next check in ${fmtSec(next)}`);
            this._setState('no-picture', {
                reason: `No new picture - the phone's screen may be off or locked (checked ${s.heldProbes}x, no reconnect)`,
                silentSince: since, held: true,
            });
        }
        if (s.heldProbes && t - since >= HEAL.DIRECT_HOLD_MAX_MS) {
            this._closeForRetry(`no picture for ${fmtSec(t - since)} on the direct route`);
            return;
        }
        const gaps = HEAL.HOLD_PROBE_GAPS_MS;
        const gap = s.heldProbes ? gaps[Math.min(s.heldProbes - 1, gaps.length - 1)] : HEAL.PROBE_MIN_GAP_MS;
        if (!s.probePending && t - ref >= HEAL.PROBE_AFTER_MS && t - this._lastProbeAt >= gap) {
            this._sendProbe(s, ref);
        }
        if (!s.lastFrameAt) {
            if (t - s.initialAt >= HEAL.NO_PICTURE_MS && this.status.state !== 'no-picture') {
                this._setState('no-picture', { reason: 'No picture from the phone yet', silentSince: s.initialAt });
            }
            return;
        }
        if (t - s.lastFrameAt >= HEAL.PROBE_AFTER_MS && this.status.state === 'streaming' && !this.status.unchanged) {
            this._setState('streaming', { unchanged: true });
        }
    }

    /**
     * MF7 AUTO re-probe: a TRIAL stream (created with heal off on the direct route) that produced its first frame becomes the
     * tile's stream. Its socket, decoder and canvas are kept - no reconnect, no gap - and self-heal is switched on for it.
     */
    promote(opts = {}) {
        this.heal = !!opts.heal;
        this.hold = !!opts.hold;
        this.autoFallback = !!opts.autoFallback && this.installId != null && !this.url;
        this._fellBack = false;
        if (opts.remoteLow) this.remoteLow = opts.remoteLow;
        if (opts.remoteHigh !== undefined) this.remoteHigh = opts.remoteHigh;
        this.onState = typeof opts.onState === 'function' ? opts.onState : null;
        this.onAuthSuspect = typeof opts.onAuthSuspect === 'function' ? opts.onAuthSuspect : null;
        this.onHint = typeof opts.onHint === 'function' ? opts.onHint : null;
        if (opts.log) this.log = opts.log;
        this.logTag = '';
        this._attempt = 0;
        this._slowTries = 0;
        if (this.ws && (this.heal || this.autoFallback)) this._startTick();
        this._setState(this.status.state, {});
    }

    _resetDecoder() {
        this._configured = false;
        this._hadIDR = false;
        this._buffer = null;
        if (this.decoder && this.decoder.state !== 'closed') {
            try {
                this.decoder.close();
            } catch (e) {
                /* noop */
            }
        }
        this.decoder = null;
    }

    _setState(state, patch = {}) {
        const prev = this.status;
        const changed = prev.state !== state;
        const base = changed
            ? { ...prev, reason: null, nextAt: null, gaveUp: null, unchanged: false, scheduled: false }
            : prev;
        this.status = Object.freeze({
            ...base,
            ...patch,
            state,
            route: this._route(),
            since: changed ? nowMs() : prev.since,
        });
        if (this.onState) {
            try {
                this.onState(this.status);
            } catch (e) {
                /* a listener must never break the stream */
            }
        }
    }

    _log(msg) {
        const route = this._route() === 'local' ? 'direct' : this._route();
        try {
            this.log(`[monitor] ${this.label || 'PC'}/${this.serial} ${route}: ${this.logTag}${msg}`);
        } catch (e) {
            /* noop */
        }
    }

    stop() {
        this._stopped = true;
        this._clearTimer('retry');
        this._clearTimer('start');
        this._clearTimer('tick');
        if (this.ws) {
            const ws = this.ws;
            this.ws = null;
            try {
                ws.onmessage = null;
                ws.close();
            } catch (e) {
                /* noop */
            }
        }
        this._sock = null;
        this._resetDecoder();
        this._setState('stopped');
    }

    isOpen() {
        return !!this.ws && this.ws.readyState === WebSocket.OPEN;
    }

    stats() {
        return {
            serial: this.serial,
            transport: this.transport,
            installId: this.installId,
            quality: this.quality,
            videoBytes: this.videoBytes,
            totalBytes: this.totalBytes,
            settingsSent: this.settingsSent,
            opens: this.opens,
            closes: this.closes,
            frames: this.frames,
            open: this.isOpen(),
            error: this._lastError,
            state: this.status ? this.status.state : 'idle',
            route: this._route(),
            attempt: this._attempt,
            lastFrameAt: this.lastFrameAt,
            firstFrameMs: this.firstFrameMs,
            fellBack: this._fellBack,
            hold: this.hold,
            pcHeals: !!(this._sock && this._sock.pcHeals),
            heldProbes: this._sock ? this._sock.heldProbes : 0,
        };
    }

    _emit(kind) {
        this.onStatus({ kind, ...this.stats() });
    }
}

function concatU8(a, b) {
    if (!a) return b.slice();
    const out = new Uint8Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return out;
}
